import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, Clock, Loader2, MailCheck, ShieldOff } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { LiveNetwork } from '../components/LiveNetwork';
import { api } from '../lib/api';
import { useSession } from '../lib/session';

/**
 * The four places a provider sign-in can land somebody who is not let in.
 *
 * Each is a page with words on it rather than a JSON error, because these URLs
 * are opened by a person who has just pressed a button and is waiting to find
 * out what happened. A raw 400 in the address bar is how somebody concludes the
 * company's system is broken and rings the office.
 *
 * They share a frame with the sign-in page — the same live network on the left —
 * so the flow does not feel like it has thrown you out of the application.
 */

function Frame({
  icon: Icon,
  headline,
  children,
}: {
  icon: typeof Clock;
  headline: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-screen">
      <div className="relative hidden w-1/2 bg-navy-700 lg:block">
        <LiveNetwork />
      </div>

      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-md space-y-5">
          <img src="/brand/logo.png" alt="GoSpeedNet" className="h-10 w-auto" />

          <div
            className="flex h-12 w-12 items-center justify-center rounded-xl border
              border-navy-200 bg-card"
          >
            <Icon className="h-6 w-6 text-navy-600" aria-hidden="true" />
          </div>

          <h1 className="font-heading text-2xl text-navy-700">{headline}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * "We have your request."
 *
 * The one thing this page must do is stop somebody pressing the button again.
 * So it says plainly that a person has to look at it, and that an email is
 * coming either way — a "waiting" screen with no expectations set is what
 * produces four duplicate requests and a phone call.
 */
export function AwaitingApprovalPage() {
  const [params] = useSearchParams();
  const isNew = params.get('new') === '1';

  return (
    <Frame icon={Clock} headline={isNew ? 'Request sent' : 'Still waiting'}>
      <p className="text-sm">
        {isNew
          ? 'Thank you — we have your request.'
          : 'Your request is still with us; you do not need to send another.'}
      </p>
      <p className="text-sm text-muted-foreground">
        Somebody here has to approve it and choose what you will be able to do. That happens during
        office hours. You will get an email either way, and if it is approved that email carries the
        link that finishes setting up your account.
      </p>
      <p className="text-sm text-muted-foreground">
        Nothing more is needed from you now. There is no account to sign into yet.
      </p>
      <Link to="/login" className="btn-secondary">
        Back to sign in
      </Link>
    </Frame>
  );
}

/**
 * Approved, but the link in the email has not been followed.
 *
 * A distinct page from the one above, because the two are genuinely different
 * situations with different remedies: one is waiting on us, the other is
 * waiting on them, and telling somebody to "wait for approval" when the
 * approval email is already sitting in their inbox wastes a day.
 */
export function AwaitingActivationPage() {
  return (
    <Frame icon={MailCheck} headline="Check your email">
      <p className="text-sm">Your access has been approved.</p>
      <p className="text-sm text-muted-foreground">
        There is one step left: open the link in the approval email to confirm the account. Until
        that is done the account exists but cannot be used — which is deliberate, so an approval
        alone never lets anybody in.
      </p>
      <p className="text-sm text-muted-foreground">
        If the email has not arrived, look in the spam folder first. If it has expired, an admin can
        send another.
      </p>
      <Link to="/login" className="btn-secondary">
        Back to sign in
      </Link>
    </Frame>
  );
}

/**
 * Refused, for one of several reasons the server names.
 *
 * The reason arrives in the query string because it is composed on the server —
 * the console must not guess at why, and the sentences it would guess would be
 * vaguer than the real ones.
 */
export function AccessRefusedPage() {
  const [params] = useSearchParams();
  const reason = params.get('reason');

  return (
    <Frame icon={ShieldOff} headline="Not signed in">
      <p className="text-sm">
        {reason ??
          'That sign-in could not be completed. Please try again from the sign-in page.'}
      </p>
      <Link to="/login" className="btn-secondary">
        Back to sign in
      </Link>
    </Frame>
  );
}

/**
 * The activation link.
 *
 * ## Why this asks before doing anything
 *
 * The first version posted the token automatically on mount, and that was wrong
 * twice over. It shipped, and it produced exactly the failure it deserved: an
 * account was activated and signed in at 18:20:29.919, a second POST arrived
 * half a second later, found the single-use token spent, and its error replaced
 * the success on screen. The person was told **"That link did not work"** while
 * holding a working session for an account that had just been created for them.
 * They gave up on something that worked.
 *
 * Two independent fixes, because one of them alone would leave the other bug:
 *
 *  1. **Nothing happens until a person presses a button.** A URL in an email is
 *     fetched by things that are not people — Gmail's own scanner, corporate
 *     antivirus proxies, link previews, the browser speculatively preloading.
 *     Any of those loading a page that posts on sight would confirm somebody's
 *     account before they had read the message. The original code carried a
 *     comment claiming the page "asks" precisely so this could not happen; the
 *     page did not ask. Now it does.
 *  2. **A failure is checked against the server before it is believed.** If the
 *     POST errors but a session now exists, the activation worked — whatever
 *     went wrong afterwards. That covers the double-submit, a retry, a flaky
 *     connection dropping the response, and anything else of that shape.
 */
export function ActivatePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { refresh } = useSession();

  /** Set when a failed POST turns out to have worked anyway. */
  const [salvaged, setSalvaged] = useState(false);

  const activate = useMutation({
    mutationFn: () => api.activateAccount(token ?? ''),
    onSuccess: async () => {
      // The POST set the session cookies, so the console has to be told who it
      // is now before it can route anywhere useful.
      await refresh();
      setTimeout(() => navigate('/', { replace: true }), 1200);
    },
    onError: async () => {
      /*
       * "Already used" is not the same as "did not work".
       *
       * The commonest cause of a spent token is this very page having spent it a
       * moment ago. So before telling somebody their link is dead, ask the
       * server who we are: a session means it worked.
       */
      try {
        const me = await api.me();
        if (me?.user) {
          setSalvaged(true);
          await refresh();
          setTimeout(() => navigate('/', { replace: true }), 1200);
        }
      } catch {
        // Genuinely not signed in. The error below is the right thing to show.
      }
    },
  });

  if (activate.isSuccess || salvaged) {
    return (
      <Frame icon={CheckCircle2} headline="You are in">
        <p className="text-sm">
          {activate.data?.alreadyActivated || salvaged
            ? 'This account is confirmed — signing you in.'
            : `Confirmed. Welcome, ${activate.data?.name ?? 'and welcome'}.`}
        </p>
        <p className="text-sm text-muted-foreground">Taking you to the console.</p>
      </Frame>
    );
  }

  if (activate.isError) {
    return (
      <Frame icon={ShieldOff} headline="That link did not work">
        <p className="text-sm">{(activate.error as Error).message}</p>
        <p className="text-sm text-muted-foreground">
          Activation links work once and expire after two days. An admin can send you another, or
          give you the link directly.
        </p>
        <Link to="/login" className="btn-secondary">
          Back to sign in
        </Link>
      </Frame>
    );
  }

  return (
    <Frame icon={MailCheck} headline="Confirm your account">
      <p className="text-sm">
        One press and the account is yours. This is the last step.
      </p>
      {/* The one orange thing on the screen, on the only action there is. */}
      <button
        type="button"
        className="btn-primary"
        disabled={activate.isPending || !token}
        onClick={() => activate.mutate()}
      >
        {activate.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {activate.isPending ? 'Confirming…' : 'Confirm my account'}
      </button>
      <p className="text-sm text-muted-foreground">
        Afterwards, sign in the same way you signed up. The link works once and expires two days
        after it was sent.
      </p>
    </Frame>
  );
}
